import { useMemo, useState } from 'react'
import type { CommercialStatus, ProductRow } from '../../../types'
import { BADGE_BASE } from '../../layout/mobile/mobileTokens'
import { commercialStatusColor, AI_BTN, AI_PANEL, AI_LABEL } from '../../../lib/statusStyles'

const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const fmtNum1 = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const STATUS_RANK: Record<CommercialStatus, number> = {
  'Compra urgente': 0,
  'Comprar':        1,
  'Monitorar':      2,
  'Não comprar':    3,
}

interface SupplierGroup {
  supplierName: string
  rows: ProductRow[]
}

interface Props {
  groups: SupplierGroup[]
  qtyOf: (r: ProductRow) => number
  setQty: (id: string, qty: number) => void
  aiSupplierBusy: string | null
  aiBusyId: string | null
  aiTextById: Record<string, string>
  busy: string | null
  onAnalisarFornecedor: (supplierName: string, rows: ProductRow[]) => void
  onExplicar: (r: ProductRow) => void
  onAprovar: (r: ProductRow) => void
  onMonitorar: (r: ProductRow) => void
  onBloquear: (r: ProductRow) => void
}

export default function MobilePurchaseSuggestion(props: Props) {
  const { groups } = props
  const [selected, setSelected] = useState<string | null>(null)

  const selectedGroup = useMemo(
    () => (selected ? groups.find(g => g.supplierName === selected) ?? null : null),
    [groups, selected]
  )

  if (selectedGroup) {
    return (
      <SupplierDetail
        {...props}
        group={selectedGroup}
        onBack={() => setSelected(null)}
      />
    )
  }

  return <SupplierList groups={groups} qtyOf={props.qtyOf} onOpen={setSelected} />
}

// ─── Lista enxuta de fornecedores ─────────────────────────────────────────

interface ListProps {
  groups: SupplierGroup[]
  qtyOf: (r: ProductRow) => number
  onOpen: (supplierName: string) => void
}

function SupplierList({ groups, qtyOf, onOpen }: ListProps) {
  return (
    <div className="space-y-2.5">
      <p className="text-[11px] text-ink-faint px-1">
        Toque em um fornecedor para abrir os produtos e análise IA.
      </p>
      {groups.map(g => {
        const subtotal = g.rows.reduce((s, r) => s + qtyOf(r) * r.cost, 0)
        const urgentes = g.rows.filter(r => r.commercial_status === 'Compra urgente').length
        const lt = g.rows[0]?.suppliers?.lead_time_days
        return (
          <button
            key={g.supplierName}
            onClick={() => onOpen(g.supplierName)}
            className="w-full text-left bg-surface active:bg-sunken border border-line border-l-4 border-l-brand-500 rounded-2xl p-3.5 touch-action-manip no-tap-highlight"
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-ink font-bold text-sm leading-tight line-clamp-2 min-w-0 flex-1">
                {g.supplierName}
              </h3>
              <span className="shrink-0 text-ink-faint text-lg leading-none">›</span>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-sunken text-ink-muted font-medium">
                {g.rows.length} {g.rows.length === 1 ? 'item' : 'itens'}
              </span>
              {urgentes > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-danger/10 border border-danger/40 text-danger font-semibold">
                  🚨 {urgentes} urgente{urgentes > 1 ? 's' : ''}
                </span>
              )}
              {lt != null && (
                <span className="text-ink-faint">LT {lt}d</span>
              )}
              <span className="ml-auto text-brand-700 dark:text-brand-300 font-bold tabular-nums">{fmtMoney(subtotal)}</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ─── Detalhe do fornecedor (drill-in) ─────────────────────────────────────

interface DetailProps extends Props {
  group: SupplierGroup
  onBack: () => void
}

function SupplierDetail({
  group, qtyOf, setQty,
  aiSupplierBusy, aiBusyId, aiTextById, busy,
  onAnalisarFornecedor, onExplicar, onAprovar, onMonitorar, onBloquear,
  onBack,
}: DetailProps) {
  const subtotal = group.rows.reduce((s, r) => s + qtyOf(r) * r.cost, 0)
  const lt = group.rows[0]?.suppliers?.lead_time_days

  // Ordena por urgência: urgente → comprar → monitorar → não comprar
  const sortedRows = useMemo(
    () => [...group.rows].sort((a, b) =>
      (STATUS_RANK[a.commercial_status] ?? 9) - (STATUS_RANK[b.commercial_status] ?? 9)
    ),
    [group.rows]
  )

  return (
    <div className="space-y-3">
      {/* Header sticky com Voltar + chip Analisar */}
      <div className="sticky top-0 -mx-3 px-3 py-2 bg-paper/95 backdrop-blur-sm z-10 border-b border-line">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-sunken active:bg-line text-ink text-sm font-medium touch-action-manip"
          >
            <span aria-hidden>←</span>
            <span>Voltar</span>
          </button>
          <button
            onClick={() => onAnalisarFornecedor(group.supplierName, group.rows)}
            disabled={aiSupplierBusy === group.supplierName}
            className={`ml-auto px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 touch-action-manip ${AI_BTN}`}
          >
            {aiSupplierBusy === group.supplierName ? (
              <>
                <span className="w-3 h-3 border border-brand-900/50 border-t-transparent rounded-full animate-spin" />
                Analisando…
              </>
            ) : (
              <>✨ Analisar fornecedor</>
            )}
          </button>
        </div>
        <div className="mt-2 min-w-0">
          <h3 className="text-ink font-bold text-base leading-tight line-clamp-2">{group.supplierName}</h3>
          <div className="flex items-center gap-2 mt-0.5 text-[11px]">
            <span className="text-ink-faint">{group.rows.length} itens{lt != null ? ` · LT ${lt}d` : ''}</span>
            <span className="ml-auto text-brand-700 dark:text-brand-300 font-bold tabular-nums">{fmtMoney(subtotal)}</span>
          </div>
        </div>
      </div>

      {/* Cards de produto */}
      <div className="space-y-2.5">
        {sortedRows.map(r => (
          <div key={r.id} className="bg-surface border border-line border-l-4 border-l-brand-500 rounded-2xl p-3.5">
            <p className="text-ink text-[15px] font-semibold leading-tight line-clamp-2 mb-1">
              {r.name}
            </p>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`${BADGE_BASE} ${commercialStatusColor[r.commercial_status]}`}>
                {r.commercial_status}
              </span>
              {r.demanda_prevista > r.monthly_avg * 1.05 && (
                <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" title={`Demanda acelerando: previsão ${fmtNum1(r.demanda_prevista)}/mês vs média ${fmtNum1(r.monthly_avg)}/mês — sugestão antecipada`}>
                  📈
                </span>
              )}
              <span className="text-[10px] text-ink-faint font-mono" title={r.sku}>{r.sku}</span>
              <span className="text-[10px] text-ink-faint ml-auto">Score {r.commercial_score}</span>
            </div>

            <div className="grid grid-cols-3 gap-px bg-line rounded-lg overflow-hidden text-center mb-2.5">
              <div className="bg-sunken py-1.5">
                <p className="text-[9px] text-ink-faint uppercase">Estoque</p>
                <p className={`text-sm font-bold tabular-nums ${r.current_stock <= 0 ? 'text-danger' : 'text-ink'}`}>
                  {r.current_stock}
                </p>
              </div>
              <div className="bg-sunken py-1.5">
                <p className="text-[9px] text-ink-faint uppercase">Estq Máx</p>
                <p className="text-sm font-bold tabular-nums text-ink-muted">{fmtNum1(r.estoque_max)}</p>
              </div>
              <div className="bg-sunken py-1.5">
                <p className="text-[9px] text-ink-faint uppercase">Sugestão</p>
                <p className="text-sm font-bold tabular-nums text-brand-700 dark:text-brand-300">{r.total_suggestion}</p>
                {r.filial_suggestion > 0 && (
                  <p className="text-[9px] text-ink-faint tabular-nums" title="Matriz + Filiais">
                    M{r.purchase_suggestion} + F{r.filial_suggestion}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => setQty(r.id, Math.max(0, qtyOf(r) - 1))}
                className="w-10 h-10 rounded-lg bg-sunken active:bg-line text-ink text-xl font-bold flex items-center justify-center touch-action-manip"
              >−</button>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={qtyOf(r)}
                onChange={e => setQty(r.id, Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                className="flex-1 h-10 bg-sunken border border-line rounded-lg text-center text-ink text-base font-bold tabular-nums"
              />
              <button
                onClick={() => setQty(r.id, qtyOf(r) + 1)}
                className="w-10 h-10 rounded-lg bg-sunken active:bg-line text-ink text-xl font-bold flex items-center justify-center touch-action-manip"
              >+</button>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              <button
                onClick={() => onAprovar(r)}
                disabled={busy === r.id}
                className="h-10 rounded-lg bg-brand-600/15 active:bg-brand-600/25 disabled:opacity-50 text-brand-700 dark:text-brand-300 text-xs font-bold border border-brand-600/40 touch-action-manip"
              >
                ✓ Aprovar
              </button>
              <button
                onClick={() => onMonitorar(r)}
                disabled={busy === r.id}
                className="h-10 rounded-lg bg-sunken active:bg-line disabled:opacity-50 text-ink-muted text-xs font-bold border border-line touch-action-manip"
              >
                Monitorar
              </button>
              <button
                onClick={() => onBloquear(r)}
                disabled={busy === r.id}
                className="h-10 rounded-lg bg-danger/10 active:bg-danger/20 disabled:opacity-50 text-danger text-xs font-bold border border-danger/40 touch-action-manip"
              >
                Bloquear
              </button>
            </div>

            <button
              onClick={() => onExplicar(r)}
              disabled={aiBusyId === r.id}
              className={`mt-2 w-full h-9 rounded-lg text-xs font-bold touch-action-manip ${AI_BTN}`}
            >
              {aiBusyId === r.id ? '...' : aiTextById[r.id] ? 'Fechar explicação' : '✨ Explicar'}
            </button>
            {aiTextById[r.id] && (
              <div className={`mt-2 p-2.5 rounded-lg ${AI_PANEL}`}>
                <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${AI_LABEL}`}>✨ IA</p>
                <p className="text-xs text-ink leading-relaxed whitespace-pre-line">{aiTextById[r.id]}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
